import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import colors from 'Common/constants/colors';
import { get } from 'lodash';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from 'Common/components/icons';
import Button from 'Common/components/Button';
import { AddSharp } from '@material-ui/icons';
import Textarea from 'Common/components/form/fields/Textarea';
import Answers from 'Education/components/instruction/test-edit/Answers';
import QuestionImage from 'Education/components/instruction/test-edit/QuestionImage';

const Wrapper = styled.div`
    padding: 10px;
    margin-bottom: 30px;
    background: ${colors.ALT_GRAY};
    border-radius: 4px;
`;

const QuestionTopBar = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const QuestionNumber = styled.div`
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.LIGHTER_GRAY};
`;

const QuestionInfo = styled.div`
    margin-bottom: 20px;

    @media screen and (min-width: 680px) {
        display: flex;
    }
`;

const QuestionTitle = styled.div`
    @media screen and (min-width: 680px) {
        flex: 1;
        order: 1;
        margin-right: 10px;
    }
`;

const QuestionTitleTextarea = styled(Textarea)`
    min-height: 120px;
`;

const QuestionImageWrapper = styled.div`
    height: 160px;
    margin-bottom: 10px;

    @media screen and (min-width: 680px) {
        height: auto;
        margin-bottom: 0;
        flex: 0 0 200px;
        order: 2;
    }
`;

const ActionButton = styled.button`
    flex: 0 0 15px;
    margin-left: 7px;
    padding: 0 3px;
    background: none;
    border: 0 none;
    color: ${colors.LIGHT_GRAY};
    cursor: pointer;

    &:hover,
    &:focus {
        outline: 0 none;
        color: ${colors.WHITE};
    }
`;

const AddAnswerButton = styled(Button)`
    width: auto;
`;

const Question = ({
    questionValues,
    errors,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
    number,
    isFirst,
    isLast,
    fieldPrefix,
    onImageChange,
    onMoveBackwards,
    onMoveForwards,
    onDelete,
    onAnswerAdd,
    onAnswerDelete,
}) => (
    <Wrapper>
        <QuestionTopBar>
            <QuestionNumber>{`Frage ${number}`}</QuestionNumber>

            {!isFirst && (
                <ActionButton onClick={onMoveBackwards}>
                    <ArrowUpIcon size="xs" />
                </ActionButton>
            )}
            {!isLast && (
                <ActionButton onClick={onMoveForwards}>
                    <ArrowDownIcon size="xs" />
                </ActionButton>
            )}
            <ActionButton onClick={onDelete}>
                <TrashIcon size="xs" />
            </ActionButton>
        </QuestionTopBar>
        <div>
            <QuestionInfo>
                <QuestionImageWrapper>
                    <QuestionImage
                        defaultImage={questionValues.image}
                        onImageChange={onImageChange}
                    />
                </QuestionImageWrapper>
                <QuestionTitle>
                    <QuestionTitleTextarea
                        label="Frage"
                        id={`${fieldPrefix}.title`}
                        name={`${fieldPrefix}.title`}
                        error={get(errors, `${fieldPrefix}.title`)}
                        touched={get(touched, `${fieldPrefix}.title`, false)}
                        value={questionValues.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </QuestionTitle>
            </QuestionInfo>
            <Answers
                answers={questionValues.answers}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                fieldPrefix={`${fieldPrefix}.answers`}
                onAnswerDelete={onAnswerDelete}
            />
            <AddAnswerButton
                type="button"
                icon={AddSharp}
                onClick={onAnswerAdd}
            >
                Antwort hinzufügen
            </AddAnswerButton>
        </div>
    </Wrapper>
);

Question.propTypes = {
    questionValues: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
    number: PropTypes.number.isRequired,
    isFirst: PropTypes.bool.isRequired,
    isLast: PropTypes.bool.isRequired,
    fieldPrefix: PropTypes.string.isRequired,
    onImageChange: PropTypes.func.isRequired,
    onMoveBackwards: PropTypes.func.isRequired,
    onMoveForwards: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAnswerAdd: PropTypes.func.isRequired,
    onAnswerDelete: PropTypes.func.isRequired,
};

export default Question;
